import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { 
  List, 
  FileText, 
  Search, 
  FileCheck, 
  File, 
  BookOpen, 
  Shield, 
  Gavel, 
  PenTool, 
  Calculator,
  Eye,
  Trash2,
  ArrowLeft
} from 'lucide-react';
import { worklistAPI } from '../services/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';

const getTypeIcon = (type) => {
  const iconMap = {
    'document-analysis': FileText,
    'legal-research': Search,
    'case-summary': FileCheck,
    'contract-review': File,
    'legal-brief': BookOpen,
    'compliance': Shield,
    'precedents': Gavel,
    'document-draft': PenTool,
    'cost-estimate': Calculator
  };
  
  const IconComponent = iconMap[type] || FileText;
  return <IconComponent size={16} />;
};

const getTypeLabel = (type) => {
  const labelMap = {
    'document-analysis': 'Document Analysis',
    'legal-research': 'Legal Research',
    'case-summary': 'Case Summary',
    'contract-review': 'Contract Review',
    'legal-brief': 'Legal Brief',
    'compliance': 'Compliance Check',
    'precedents': 'Precedent Search',
    'document-draft': 'Document Draft',
    'cost-estimate': 'Cost Estimate'
  };
  
  return labelMap[type] || type;
};

const WorklistList = () => {
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadWorkItems();
  }, []);

  const loadWorkItems = async () => {
    try {
      const response = await worklistAPI.getWorkItems();
      setWorkItems(response.workItems || []);
    } catch (error) {
      console.error('Failed to load work items:', error);
      toast.error('Failed to load work items');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, event) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await worklistAPI.deleteWorkItem(id);
      setWorkItems(items => items.filter(item => item._id !== id));
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading work items...</div>;
  }

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <h2>Worklist</h2>
          <p>View all your completed legal AI work</p>
        </div>
        <div className="user-info">
          <span>{workItems.length} total items</span>
        </div>
      </div>

      <div className="tool-content">
        <div className="tool-header">
          <List size={24} />
          <h3>All Work Items</h3>
        </div>

        {workItems.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {workItems.map((item) => (
              <div
                key={item._id}
                className="activity-item"
                onClick={() => navigate(`/dashboard/worklist/${item._id}`)}
                style={{ 
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <div className="activity-icon">
                  {getTypeIcon(item.type)}
                </div>
                <div className="activity-content" style={{ flex: 1 }}>
                  <div className="activity-title" style={{ marginBottom: '4px' }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
                    {getTypeLabel(item.type)}
                  </div>
                  <div className="activity-time">
                    {formatDate(item.createdAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/worklist/${item._id}`);
                    }}
                  >
                    <Eye size={14} />
                    View
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ 
                      padding: '6px 12px', 
                      fontSize: '12px',
                      color: '#ef4444',
                      borderColor: '#ef4444'
                    }}
                    onClick={(e) => handleDelete(item._id, e)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ 
            color: '#64748b', 
            textAlign: 'center', 
            padding: '40px',
            fontStyle: 'italic'
          }}>
            No work items yet. Start using the legal AI tools to see your work here.
          </p>
        )}
      </div>
    </div>
  );
};

const WorklistDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workItem, setWorkItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkItem();
  }, [id]);

  const loadWorkItem = async () => {
    try {
      const response = await worklistAPI.getWorkItem(id);
      setWorkItem(response.workItem);
    } catch (error) {
      console.error('Failed to load work item:', error);
      toast.error('Failed to load work item');
      navigate('/dashboard/worklist');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading work item...</div>;
  }

  if (!workItem) {
    return <div className="error">Work item not found</div>;
  }

  return (
    <div>
      <div className="content-header">
        <div className="welcome-section">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard/worklist')}
            style={{ marginBottom: '12px' }}
          >
            <ArrowLeft size={16} />
            Back to Worklist
          </button>
          <h2>{workItem.title}</h2>
          <p>{getTypeLabel(workItem.type)} • {formatDate(workItem.createdAt)}</p>
        </div>
      </div>

      <div className="tool-content">
        <div className="tool-header">
          {getTypeIcon(workItem.type)}
          <h3>Input Data</h3>
        </div>
        
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          lineHeight: '1.6',
          color: '#374151',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {JSON.stringify(workItem.inputData, null, 2)}
        </div>
      </div>

      <div className="tool-content">
        <div className="tool-header">
          {getTypeIcon(workItem.type)}
          <h3>AI Generated Result</h3>
        </div>
        
        <MarkdownRenderer content={workItem.result} />

        <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigator.clipboard.writeText(workItem.result)}
          >
            Copy Result
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              const fullContent = `${workItem.title}\n${getTypeLabel(workItem.type)}\n${formatDate(workItem.createdAt)}\n\nInput:\n${JSON.stringify(workItem.inputData, null, 2)}\n\nResult:\n${workItem.result}`;
              navigator.clipboard.writeText(fullContent);
              toast.success('Full work item copied to clipboard');
            }}
          >
            Copy All
          </button>
        </div>
      </div>
    </div>
  );
};

const Worklist = () => {
  return (
    <Routes>
      <Route path="/" element={<WorklistList />} />
      <Route path="/:id" element={<WorklistDetail />} />
    </Routes>
  );
};

export default Worklist;
